<h1>Map data and services</h1>

<section id="viewers">
  <h2>Map viewers</h2>
  <mat-nav-list>
    <a mat-list-item href="https://svalbardkartet.npolar.no" target="_blank">
      <mat-icon matListItemIcon>open_in_new</mat-icon>
      <span matListItemTitle>Svalbardkartet</span>
      <span matListItemLine>Thematic maps for Svalbard</span>
    </a>

    <a mat-list-item href="https://geologi.npolar.no" target="_blank">
      <mat-icon matListItemIcon>open_in_new</mat-icon>
      <span matListItemTitle>Geological Map DML (Dronning Maud Land)</span>
      <span matListItemLine>Thematic map for Antarctica</span>
    </a>

    <a mat-list-item href="https://toposvalbard.npolar.no" target="_blank">
      <mat-icon matListItemIcon>open_in_new</mat-icon>
      <span matListItemTitle>TopoSvalbard</span>
      <span matListItemLine>Topographic map over Svalbard</span>
    </a>

    <a mat-list-item href="https://topojanmayen.npolar.no" target="_blank">
      <mat-icon matListItemIcon>open_in_new</mat-icon>
      <span matListItemTitle>TopoJanMayen</span>
      <span matListItemLine>Thematic map for Antarctica</span>
    </a>
  </mat-nav-list>
</section>

<section id="data">
  <h2>Basemap data</h2>

  <p>
    The Norwegian Polar Institute offers downloads of the official topographical basemap datasets
    for Norwegian polar land areas, under these
    <a routerLink="/geodata" fragment="terms">terms of use</a>. The data are either scale
    independent or created for best visual appearance when printed at selected map scales (see
    description column). The data are provided in the most commonly used GIS file formats, to be
    used e.g. to create custom maps or to do analyses. NOTE: The downloads are not maps, but rather
    map <em>data</em> which can be used to create maps from scratch in GIS software. Click the links
    below to go to data product page for metadata and data downloads. Partners in Norway digital
    (Norge digitalt): Please download data from the Geonorge website instead.
  </p>

  <h3>Svalbard</h3>
  <mat-nav-list>
    <a mat-list-item routerLink="/dataset/63730e2e-b7a6-4d14-b341-c661ccdc5254">
      <mat-icon matListItemIcon>article</mat-icon>
      <span matListItemTitle>S1000 Kartdata</span>
      <span matListItemLine>Svalbard 1:1 000 000 basemap themes</span>
      <span matListItemLine>Vector</span>
    </a>
    <a mat-list-item routerLink="/dataset/a23acc28-288b-49ba-ac6d-025d1fdee246">
      <mat-icon matListItemIcon>article</mat-icon>
      <span matListItemTitle>S250 Kartdata</span>
      <span matListItemLine>Svalbard 1:250 000 basemap themes</span>
      <span matListItemLine>Vector</span>
    </a>
    <a mat-list-item routerLink="/dataset/645336c7-adfe-4d5a-978d-9426fe788ee3">
      <mat-icon matListItemIcon>article</mat-icon>
      <span matListItemTitle>S100 Kartdata</span>
      <span matListItemLine>Svalbard 1:100 000 basemap themes</span>
      <span matListItemLine>Vector</span>
    </a>
    <a mat-list-item routerLink="/dataset/44ca8c2a-22c2-49e8-a50b-972734f287e3">
      <mat-icon matListItemIcon>article</mat-icon>
      <span matListItemTitle>S100 Raster</span>
      <span matListItemLine>Svalbard 1:100 000 raster map</span>
      <span matListItemLine>Raster</span>
    </a>
    <a mat-list-item routerLink="/dataset/f8e14c1b-a91b-45db-9a27-7ec652a801c9">
      <mat-icon matListItemIcon>article</mat-icon>
      <span matListItemTitle>S0 Stedsnavn</span>
      <span matListItemLine>Svalbard place names</span>
      <span matListItemLine>Vector</span>
    </a>
    <a mat-list-item routerLink="/dataset/dce53a47-c726-4845-85c3-a65b46fe2fea">
      <mat-icon matListItemIcon>article</mat-icon>
      <span matListItemTitle>S0 Terrengmodell</span>
      <span matListItemLine>Svalbard digital elevation models</span>
      <span matListItemLine>Raster</span>
    </a>
    <a mat-list-item routerLink="/dataset/e02f479b-fbb8-4860-ab8d-c5e0bbbf559c">
      <mat-icon matListItemIcon>article</mat-icon>
      <span matListItemTitle>Satellittbildemosaikk</span>
      <span matListItemLine>Offline version of NP_Satellitt_Svalbard_WMTS_25833</span>
      <span matListItemLine>Raster</span>
    </a>
  </mat-nav-list>

  <h3>Jan Mayen</h3>
  <mat-nav-list>
    <a mat-list-item routerLink="/dataset/d4b774f2-77b3-4ac2-9f16-520905aecfe8">
      <mat-icon matListItemIcon>article</mat-icon>
      <span matListItemTitle>J1000 Kartdata</span>
      <span matListItemLine>Jan Mayen 1:1 000 000 basemap themes</span>
      <span matListItemLine>Vector</span>
    </a>
    <a mat-list-item routerLink="/dataset/3d1b17f3-a22e-478e-8e5d-4a1118a1e8b0">
      <mat-icon matListItemIcon>article</mat-icon>
      <span matListItemTitle>J50 Kartdata</span>
      <span matListItemLine>Jan Mayen 1:50 000 basemap themes</span>
      <span matListItemLine>Vector</span>
    </a>
    <a mat-list-item routerLink="/dataset/34da6f13-6af4-4dab-8c6a-1bcb2c83685f">
      <mat-icon matListItemIcon>article</mat-icon>
      <span matListItemTitle>J50 Raster</span>
      <span matListItemLine>Jan Mayen 1:50 000 raster map</span>
      <span matListItemLine>Raster</span>
    </a>
    <a mat-list-item routerLink="/dataset/3534b12b-cdbb-469e-90ba-b50b797ded36">
      <mat-icon matListItemIcon>article</mat-icon>
      <span matListItemTitle>J0 Stedsnavn</span>
      <span matListItemLine>Jan Mayen place names</span>
      <span matListItemLine>Vector</span>
    </a>
    <a mat-list-item routerLink="/dataset/e2b2417e-9926-4519-b6a9-7eefb3bb1012">
      <mat-icon matListItemIcon>article</mat-icon>
      <span matListItemTitle>J0 Terrengmodell</span>
      <span matListItemLine>Jan Mayen digital elevation model</span>
      <span matListItemLine>Raster</span>
    </a>
  </mat-nav-list>

  <h3>Dronning Maud Land</h3>
  <mat-nav-list>
    <a mat-list-item routerLink="/dataset/d45274ca-9ab7-43e0-8da5-d59cd7744d37">
      <mat-icon matListItemIcon>article</mat-icon>
      <span matListItemTitle>D250 Kartdata</span>
      <span matListItemLine
        >Dronning Maud Land 1:250 000 basemap themes, incorporated into the
        <a href="https://add.scar.org" target="_blank" (click)="$event.stopPropagation()"
          >Antarctic Digital Database</a
        ></span
      >
      <span matListItemLine>Vector</span>
    </a>
    <a mat-list-item routerLink="/dataset/f41c15a8-da37-417e-b10d-d1b5f6eb1c7c">
      <mat-icon matListItemIcon>article</mat-icon>
      <span matListItemTitle>D0 Stedsnavn</span>
      <span matListItemLine>Dronning Maud Land place names</span>
      <span matListItemLine>Vector</span>
    </a>
  </mat-nav-list>

  <h3>Bouvetøya</h3>
  <mat-nav-list>
    <a mat-list-item routerLink="/dataset/19700322-beab-40e7-b867-c3f0c2756071">
      <mat-icon matListItemIcon>article</mat-icon>
      <span matListItemTitle>B20 Kartdata</span>
      <span matListItemLine>Bouvetøya 1:20 000 basemap themes</span>
      <span matListItemLine>Vector</span>
    </a>

    <a mat-list-item routerLink="/dataset/baeb9929-cd30-4520-8d5e-2b731f82d351">
      <mat-icon matListItemIcon>article</mat-icon>
      <span matListItemTitle>B0 Stedsnavn</span>
      <span matListItemLine>Bouvetøya place names</span>
      <span matListItemLine>Vector</span>
    </a>

    <a mat-list-item routerLink="/dataset/63eaa452-c264-4bd8-8ebd-97e09e1a5a1a">
      <mat-icon matListItemIcon>article</mat-icon>
      <span matListItemTitle>B0 Terrengmodell</span>
      <span matListItemLine>Bouvetøya digital elevation model</span>
      <span matListItemLine>Raster</span>
    </a>
  </mat-nav-list>

  <h3>Peter I Øy</h3>
  <mat-nav-list>
    <a mat-list-item routerLink="/dataset/29105abc-5309-498e-b3fd-e69de6814122">
      <mat-icon matListItemIcon>article</mat-icon>
      <span matListItemTitle>P50 Kartdata</span>
      <span matListItemLine>Peter I Øy 1:50 000 basemap themes</span>
      <span matListItemLine>Vector</span>
    </a>

    <a mat-list-item routerLink="/dataset/fa895997-74d8-490f-ab1d-01349b221b16">
      <mat-icon matListItemIcon>article</mat-icon>
      <span matListItemTitle>P0 Stedsnavn</span>
      <span matListItemLine>Peter I Øy place names</span>
      <span matListItemLine>Vector</span>
    </a>

    <a mat-list-item routerLink="/dataset/e4ae1d26-191a-481c-9f6a-c3fbeb752f34">
      <mat-icon matListItemIcon>article</mat-icon>
      <span matListItemTitle>P0 Terrengmodell</span>
      <span matListItemLine>Peter I Øy digital elevation model</span>
      <span matListItemLine>Raster</span>
    </a>
  </mat-nav-list>
</section>

<section id="basemap">
  <h2>Basemap services</h2>

  <p>
    The Norwegian Polar Institute offers a selection of cached and dynamic basemap services which
    can be consumed to create interactive web mapping applications or as layers in desktop GIS,
    under these <a routerLink="/geodata" fragment="terms">terms of use</a>. The cached services are
    <a
      target="_blank"
      href="https://server.arcgis.com/en/server/latest/publish-services/windows/what-is-map-caching-.htm"
      >tiled map services</a
    >
    which are maps that have been predrawn by the server at defined scales (zoom levels) and stored
    as graphic files to serve maps fast. The dynamic services are
    <a
      target="_blank"
      href="https://server.arcgis.com/en/server/latest/publish-services/windows/wms-services.htm"
      >WMS services</a
    >, which return map images as requested by the user, consisting of the active map layers, map
    extent and zoom level at the client side.
  </p>
  <p>
    The cached services can be used in clients with support for ArcGIS Server tiles such as
    OpenLayers, Google Maps or Esri&#39;s JavaScript API in the same way as Google Maps or Esri Maps
    is built up.
  </p>
  <p>
    The dynamic services, as well as the cached services, can be added as layers in most mainstream
    GIS software, such as described here for
    <a
      target="_blank"
      href="https://desktop.arcgis.com/en/arcmap/latest/map/web-maps-and-services/adding-wms-services.htm"
      >ArcGIS</a
    >
    and
    <a
      target="_blank"
      href="https://docs.qgis.org/2.6/en/docs/user_manual/working_with_ogc/ogc_client_support.html#wms-wmts-client"
      >QGIS</a
    >.
  </p>

  <table mat-table [dataSource]="services">
    <ng-container matColumnDef="type">
      <th mat-header-cell *matHeaderCellDef>Type</th>
      <td mat-cell *matCellDef="let element">{{ element.isDynamic ? 'Dynamic' : 'Cached' }}</td>
    </ng-container>

    <ng-container matColumnDef="title">
      <th mat-header-cell *matHeaderCellDef>Service</th>
      <td mat-cell *matCellDef="let element">
        <a target="_blank" [href]="element.title.url">{{ element.title.value }}</a>
      </td>
    </ng-container>

    <ng-container matColumnDef="layers">
      <th mat-header-cell *matHeaderCellDef>Layers</th>
      <td mat-cell *matCellDef="let element">{{ element.layers }}</td>
    </ng-container>

    <ng-container matColumnDef="projection">
      <th mat-header-cell *matHeaderCellDef>Projection</th>
      <td mat-cell *matCellDef="let element">{{ element.projection }}</td>
    </ng-container>

    <ng-container matColumnDef="service">
      <th mat-header-cell *matHeaderCellDef>Service URL</th>
      <td mat-cell *matCellDef="let element">
        <a target="_blank" [href]="element.service.url">{{ element.service.value }}</a>
      </td>
    </ng-container>

    <ng-container matColumnDef="capabilities">
      <th mat-header-cell *matHeaderCellDef>Capabilities</th>
      <td mat-cell *matCellDef="let element">
        <a target="_blank" [href]="element.capabilities.url">{{ element.capabilities.value }}</a>
      </td>
    </ng-container>

    @let servicesColumns = ['title', 'type', 'layers', 'projection', 'service', 'capabilities'];

    <tr mat-header-row *matHeaderRowDef="servicesColumns"></tr>
    <tr mat-row *matRowDef="let row; columns: servicesColumns"></tr>
  </table>

  <p>
    To easily combine cache from different services in the same map window, it is an advantage to
    have a common definition of the geographic area that defines the service. Because the cached
    services have fixed zoom levels, the service can not deliver tiles that lies between two defined
    zoom levels.
  </p>
  <p>
    The table below shows the zoom levels, scales and resolutions for the cached services at the
    Norwegian Polar Institute (except the EPSG:3857 service). These are the same as in the
    <a target="_blank" href="https://www.statkart.no/Kart/Gratis-kartdata/Cache-tjenester/"
      >Norwegian Mapping Authority caches</a
    >
    for mainland Norway.
  </p>

  <table mat-table [dataSource]="scales">
    <ng-container matColumnDef="zoomLevel">
      <th mat-header-cell *matHeaderCellDef>Zoom level</th>
      <td mat-cell *matCellDef="let element">{{ element.zoomLevel }}</td>
    </ng-container>

    <ng-container matColumnDef="scale">
      <th mat-header-cell *matHeaderCellDef>Scale</th>
      <td mat-cell *matCellDef="let element">{{ element.scale }}</td>
    </ng-container>

    <ng-container matColumnDef="resolution">
      <th mat-header-cell *matHeaderCellDef>Resolution</th>
      <td mat-cell *matCellDef="let element">{{ element.resolution }}</td>
    </ng-container>

    @let scalesColumns = ['zoomLevel', 'scale', 'resolution'];

    <tr mat-header-row *matHeaderRowDef="scalesColumns"></tr>
    <tr mat-row *matRowDef="let row; columns: scalesColumns"></tr>
  </table>

  <h4>Image formats and coordinate systems</h4>

  <p>Services are available in two image formats:</p>
  <ul>
    <li>Image / jpeg (does not support transparency)</li>
    <li>Image / png (supports transparency)</li>
  </ul>

  <p>
    Tile size for jpeg is about 1/3 of the png so it must be considered what is important, speed or
    support for transparency. The image tiles have a resolution of 96 dpi with a size of (256x256
    pixels).
  </p>
</section>

<section id="thematic">
  <h2>Thematic map services</h2>

  <p>
    NPI delivers a wealth of thematic maps as dynamic map services. Dynamic map service is a service
    where a server request to send map section in the form of raster files back to the user, either
    directly in the browser, a web application or a desktop application.
  </p>

  <table mat-table [dataSource]="thematicServices">
    <ng-container matColumnDef="category">
      <th mat-header-cell *matHeaderCellDef>Category</th>
      <td mat-cell *matCellDef="let element">{{ element.category }}</td>
    </ng-container>

    <ng-container matColumnDef="title">
      <th mat-header-cell *matHeaderCellDef>Service</th>
      <td mat-cell *matCellDef="let element">
        <a target="_blank" [href]="element.title.url">{{ element.title.value }}</a>
      </td>
    </ng-container>

    <ng-container matColumnDef="description">
      <th mat-header-cell *matHeaderCellDef>Description</th>
      <td mat-cell *matCellDef="let element">{{ element.description }}</td>
    </ng-container>

    <ng-container matColumnDef="owner">
      <th mat-header-cell *matHeaderCellDef>Owner</th>
      <td mat-cell *matCellDef="let element">{{ element.owner }}</td>
    </ng-container>

    @let tservicesColumns = ['title', 'category', 'description', 'owner'];

    <tr mat-header-row *matHeaderRowDef="tservicesColumns"></tr>
    <tr mat-row *matRowDef="let row; columns: tservicesColumns"></tr>
  </table>

  <p>
    Dynamic services based on WMS specification are flexible and provides the user with many
    options, such as:
  </p>

  <ul>
    <li>Choosing which map projection will be produced in</li>
    <li>Turn on or off layers in the service.</li>
    <li>Choose scale freely</li>
    <li>Which image format and transparency to be produced</li>
    <li>Retrieving Legend of the various map layers</li>
    <li>Obtaining basic information about objects on the map from attributes (feature info) etc</li>
  </ul>

  <p>
    All services are published on NPI's
    <a target="_blank" href="https://geodata.npolar.no/arcgis/rest/services/Temadata"
      >map services directory</a
    >.
  </p>
</section>

<section id="terms">
  <h2>Terms of use</h2>

  <p>
    All users of the Norwegian Polar Institute's free map products are obliged to read and accept
    the following license and terms of use.
  </p>

  <p>
    The Norwegian Polar Institute's free map products are licensed under the
    <a target="_blank" href="http://creativecommons.org/licenses/by/4.0/"
      >Creative Commons Attribution 4.0 International (CC BY 4.0)</a
    >
    license, except the service NP_Ortofoto_Svalbard_WMTS_25833, which is licensed under the
    <a target="_blank" href="https://creativecommons.org/licenses/by-nc/4.0/"
      >Creative Commons Attribution-NonCommercial 4.0 International (CC BY-NC 4.0)</a
    >
    license.
  </p>

  <p>
    This means, inter alia, that the name of the Norwegian Polar Institute must appear in all
    occations where the products or extracts of these are used, in the following manner: © Norwegian
    Polar Institute. When practicable, a link to the Norwegian Polar Institute website should be
    provided (npolar.no). When using the NP_Satellitt_Svalbard_WMTS_25833 map service, Copernicus
    must also be credited: "Norwegian Polar Institute / Copernicus Sentinel data".
  </p>

  <p>
    The services with a "FKB" prefix contains data from several data owners. The data for the
    Longyearbyen area are owned in part by Longyearbyen local council, the Norwegian Polar
    institute, Store Norske, the Governor of Svalbard, and Telenor. Other data are owned by the
    Norwegian Polar Institute. These services are available under the CC-BY license, and the data
    owners shall be credited when maps of the different settlements are used.
  </p>

  <p>
    The free map products from the Norwegian Polar Institute are distributed as-is. The Norwegian
    Polar Institute takes no responsibility for use and further use. Users must be aware that maps
    are not always consistent with the terrain, and any use must occur with due caution and
    consideration to equipment and physical conditions. Parts of the map data are of older origin
    and not suited for navigation. Reservations are made concerning product errors.
  </p>

  <p>
    The Norwegian Polar Institute's map services are provided without any warranties concerning
    uptime and availability. The Norwegian Polar Institute reserves the right to change deliveries
    and specifications and to limit use (limiting number of service calls etc.). Furthermore, if not
    otherwise stated, the rules, regulations, and related guidelines of the Norwegian Spatial Data
    Act apply.
  </p>
</section>
